import { MediaPlayer } from '@aicon-dev/caas-media-player';
import { Button, Flex } from '@aicon-dev/caas-ui';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { ErrorCover } from '~/components/common/ErrorCover';

import { MonitorCamera } from '../libs/types';

import {
  cameraName,
  cameraStatus,
  liveBadge,
  playerBottom,
  playerCameraInfo,
  playerEmpty,
  playerWrap,
} from './cctv.monitor.css';

interface CCTVMonitorPlayerProps {
  camera?: MonitorCamera | null;
  position: number | null;
  resetTimelapse: () => void;
}

export const CCTVMonitorPlayer = (props: CCTVMonitorPlayerProps) => {
  const { camera, position, resetTimelapse } = props;
  const { t } = useTranslation('cctv_monitoring');

  const render = () => {
    if (!camera?.url) {
      return (
        <div className={playerEmpty}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="72"
            height="72"
            viewBox="0 0 72 72"
            fill="none"
          >
            <rect
              x="5"
              y="16"
              width="46.8"
              height="39.6"
              rx="7.2"
              stroke="#BFBFBF"
              strokeWidth="3"
            />
            <path d="M21 29L35.4 43.4" stroke="#BFBFBF" strokeWidth="3" strokeLinecap="round" />
            <path
              d="M35.4004 29L21.0004 43.4"
              stroke="#BFBFBF"
              strokeWidth="3"
              strokeLinecap="round"
            />
            <path
              d="M51.7998 27.6927L65.4336 21.4004C67.4724 20.4594 69.7998 21.9486 69.7998 24.1941V47.8062C69.7998 50.0516 67.4724 51.5408 65.4336 50.5999L51.7998 44.3075"
              stroke="#BFBFBF"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      );
    }

    if (camera?.type === 'stream') {
      return (
        <PlayerWrapper>
          <ErrorCover>
            <MediaPlayer
              cmasUrl={process.env.REACT_APP_CMAS_API_URL || 'https://cmas.caas.blue'}
              token={camera.result?.cmasToken || ''}
              position={position || undefined}
            />
          </ErrorCover>
        </PlayerWrapper>
      );
    }

    return (
      <video
        src={camera.url}
        loop
        controls
        muted={true}
        poster={camera.thumbnailUrl}
        preload="none"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
        }}
      />
    );
  };

  return (
    <div>
      <div className={playerWrap}>{render()}</div>
      <div className={playerBottom}>
        {camera?.type === 'stream' ? (
          <div className={playerCameraInfo}>
            <div className={cameraName}>{camera?.name}</div>
            <div className={cameraStatus({ isHealthy: camera?.isHealthy })}>
              {camera?.isHealthy ? t('cctv_monitoring:m_online') : t('cctv_monitoring:m_offline')}
            </div>
          </div>
        ) : (
          <Button block onClick={resetTimelapse}>
            <Flex align="center" justify="center">
              <div
                style={{
                  position: 'relative',
                }}
              >
                LIVE
                <div className={liveBadge}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="5"
                    height="5"
                    viewBox="0 0 5 5"
                    fill="none"
                  >
                    <circle cx="2.5" cy="2.5" r="2.5" fill="#FF6661" />
                  </svg>
                </div>
              </div>
            </Flex>
          </Button>
        )}
      </div>
    </div>
  );
};

const PlayerWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
